import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from './../api/axios';
import { useNavigate } from 'react-router-dom';

const EVENT_OPTIONS = [
  { key: 'newMessages', label: 'New inbound reply', description: 'A lead replies to a campaign conversation.' },
  { key: 'newConnections', label: 'Connection accepted', description: 'A LinkedIn invitation is accepted.' },
  { key: 'leadQualified', label: 'Lead qualified', description: 'Qualification reaches the configured threshold.' },
  { key: 'meetingBooked', label: 'Meeting booked', description: 'A tracked meeting is booked.' },
  { key: 'meetingLifecycle', label: 'Meeting lifecycle updates', description: 'Reschedules, cancellations, completions and no-shows for tracked meetings.' },
  { key: 'campaignCompleted', label: 'Campaign outcome reached', description: 'The campaign reaches its configured business objective.' },
  { key: 'agentActionProposed', label: 'Agent action proposed', description: 'Agent Mode creates an approval-required action.' },
  { key: 'agentActionApproved', label: 'Agent action approved', description: 'A proposed agent action is approved.' },
  { key: 'agentActionCompleted', label: 'Agent action completed', description: 'An approved agent action executes successfully.' },
  { key: 'agentActionFailed', label: 'Agent action failed', description: 'Agent execution requires attention.' },
];

const TeamsIntegration = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [teams, setTeams] = useState([]);
  const [channels, setChannels] = useState([]);
  const [teamId, setTeamId] = useState('');
  const [channelId, setChannelId] = useState('');
  const [preferences, setPreferences] = useState({});
  const [testMessage, setTestMessage] = useState('Leadgenflow test notification');
  const [feedback, setFeedback] = useState('');
  const [busy, setBusy] = useState(false);

  const TEAMS_CLIENT_ID = process.env.REACT_APP_TEAMS_CLIENT_ID;
  const TEAMS_TENANT_ID = process.env.REACT_APP_TEAMS_TENANT_ID || 'common';
  const TEAMS_REDIRECT_URI = `${window.location.origin}/settings`;
  const TEAMS_SCOPE = 'offline_access User.Read Team.ReadBasic.All Channel.ReadBasic.All ChannelMessage.Send';
  const TEAMS_OAUTH_URL = `https://login.microsoftonline.com/${TEAMS_TENANT_ID}/oauth2/v2.0/authorize?client_id=${encodeURIComponent(TEAMS_CLIENT_ID || '')}&response_type=code&redirect_uri=${encodeURIComponent(TEAMS_REDIRECT_URI)}&response_mode=query&scope=${encodeURIComponent(TEAMS_SCOPE)}&state=teams&prompt=select_account`;

  const loadTeams = useCallback(async () => {
    const response = await api.get('/integrations/teams/teams');
    setTeams(response.data || []);
    return response.data || [];
  }, []);

  const loadChannels = useCallback(async (selectedTeamId) => {
    if (!selectedTeamId) { setChannels([]); return []; }
    const response = await api.get(`/integrations/teams/${encodeURIComponent(selectedTeamId)}/channels`);
    setChannels(response.data || []);
    return response.data || [];
  }, []);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const { data } = await api.get('/integrations/teams/status');
      setStatus(data);
      setTeamId(data.teamId || '');
      setChannelId(data.channelId || '');
      setPreferences(data.logPreferences || {});
      if (data.connected && !data.reconnectRequired) {
        await loadTeams();
        if (data.teamId) await loadChannels(data.teamId);
      }
    } catch (error) {
      console.error('Teams status error:', error);
      setFeedback(error.response?.data?.message || 'Could not load Microsoft Teams status.');
    } finally { setBusy(false); }
  }, [loadChannels, loadTeams]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const state = params.get('state');
    const initialize = async () => {
      if (code && state === 'teams') {
        setBusy(true);
        try {
          await api.post('/integrations/teams/teams-auth', { code, redirect_uri: TEAMS_REDIRECT_URI });
          setFeedback('Microsoft Teams connected. Choose the Team, channel and events you want to receive.');
          navigate('/settings?tab=integrations&integration=teams', { replace: true });
        } catch (error) {
          setFeedback(error.response?.data?.message || 'Microsoft Teams authorization failed.');
        } finally { setBusy(false); }
      }
      await load();
    };
    initialize();
  }, [load, navigate, TEAMS_REDIRECT_URI]);

  const selectedTeam = useMemo(() => teams.find((team) => team.id === teamId), [teams, teamId]);
  const selectedChannel = useMemo(() => channels.find((channel) => channel.id === channelId), [channels, channelId]);

  const chooseTeam = async (value) => {
    setTeamId(value); setChannelId(''); setChannels([]);
    if (!value) return;
    setBusy(true);
    try { await loadChannels(value); } catch (error) { setFeedback(error.response?.data?.message || 'Could not load Teams channels.'); }
    finally { setBusy(false); }
  };

  const save = async () => {
    setBusy(true); setFeedback('');
    try {
      const { data } = await api.put('/integrations/teams/user/teams-integration', {
        teamId,
        teamName: selectedTeam?.displayName || status?.teamName || '',
        channelId,
        channelName: selectedChannel?.displayName || status?.channelName || '',
        logPreferences: preferences,
      });
      setStatus((current) => ({ ...(current || {}), ...(data.teamsIntegration || {}) }));
      setFeedback('Microsoft Teams notification settings saved.');
    } catch (error) { setFeedback(error.response?.data?.message || 'Could not save Teams settings.'); }
    finally { setBusy(false); }
  };

  const sendTest = async () => {
    if (!teamId || !channelId) return setFeedback('Choose a Team and channel first.');
    setBusy(true); setFeedback('');
    try {
      await api.post('/integrations/teams/sendMessage', { teamId, channelId, text: testMessage || 'Leadgenflow test notification' });
      setFeedback('Test notification sent successfully.');
    } catch (error) { setFeedback(error.response?.data?.message || 'Could not send the Teams test notification.'); }
    finally { setBusy(false); }
  };

  const disconnect = async () => {
    setBusy(true);
    try {
      await api.delete('/integrations/teams/connection');
      setStatus({ connected: false }); setTeams([]); setChannels([]); setTeamId(''); setChannelId(''); setPreferences({});
      setFeedback('Microsoft Teams disconnected.');
    } catch (error) { setFeedback(error.response?.data?.message || 'Could not disconnect Teams.'); }
    finally { setBusy(false); }
  };

  if (!status?.connected || status?.reconnectRequired) {
    return (
      <div className="rounded-xl border border-gray-200 bg-gray-50 p-5">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div><h4 className="font-semibold text-gray-950">{status?.reconnectRequired ? 'Reconnect Microsoft Teams' : 'Connect Microsoft Teams'}</h4><p className="mt-1 text-sm text-gray-600">Use Microsoft Graph securely without exposing OAuth tokens to the browser. Leadgenflow refreshes expiring tokens server-side.</p>{status?.lastError && <p className="mt-2 text-xs text-red-600">{status.lastError}</p>}</div>
          <button disabled={busy || !TEAMS_CLIENT_ID} onClick={() => { window.location.href = TEAMS_OAUTH_URL; }} className="shrink-0 rounded-lg bg-[#6264A7] px-4 py-2.5 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50"><i className="fab fa-microsoft mr-2" />{status?.reconnectRequired ? 'Reconnect' : 'Connect Teams'}</button>
        </div>
        {feedback && <div className="mt-4 rounded-lg bg-white p-3 text-sm text-gray-700 ring-1 ring-gray-200">{feedback}</div>}
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {feedback && <div className="rounded-lg border border-blue-100 bg-blue-50 p-3 text-sm text-blue-800">{feedback}</div>}
      <div className="grid gap-4 md:grid-cols-2">
        <div className="rounded-xl border border-gray-200 p-4">
          <label className="text-sm font-semibold text-gray-900">Team</label>
          <p className="mt-1 text-xs text-gray-500">Choose the Microsoft Team that should receive Leadgenflow events.</p>
          <select value={teamId} onChange={(event) => chooseTeam(event.target.value)} className="mt-3 w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm"><option value="">Select a Team…</option>{teams.map((team) => <option key={team.id} value={team.id}>{team.displayName}</option>)}</select>
        </div>
        <div className="rounded-xl border border-gray-200 p-4">
          <label className="text-sm font-semibold text-gray-900">Channel</label>
          <p className="mt-1 text-xs text-gray-500">Select the destination channel for notifications.</p>
          <select value={channelId} onChange={(event) => setChannelId(event.target.value)} disabled={!teamId} className="mt-3 w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm disabled:bg-gray-50"><option value="">Select a channel…</option>{channels.map((channel) => <option key={channel.id} value={channel.id}>{channel.displayName}</option>)}</select>
        </div>
      </div>

      <div className="rounded-xl border border-gray-200 p-4"><label className="text-sm font-semibold text-gray-900">Send test notification</label><div className="mt-3 flex gap-2"><input value={testMessage} onChange={(event) => setTestMessage(event.target.value)} className="min-w-0 flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm" /><button disabled={busy || !teamId || !channelId} onClick={sendTest} className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium hover:bg-gray-50 disabled:opacity-50">Send test</button></div></div>

      <div className="rounded-xl border border-gray-200 overflow-hidden">
        <div className="border-b border-gray-100 bg-gray-50 px-4 py-3"><h4 className="text-sm font-semibold text-gray-900">Events sent to Microsoft Teams</h4><p className="mt-0.5 text-xs text-gray-500">Use business events instead of noisy low-level logs. Agent actions are included in the same auditable event lifecycle.</p></div>
        <div className="divide-y divide-gray-100">{EVENT_OPTIONS.map((item) => <label key={item.key} className="flex cursor-pointer items-start justify-between gap-4 px-4 py-3 hover:bg-gray-50"><div><div className="text-sm font-medium text-gray-900">{item.label}</div><div className="mt-0.5 text-xs text-gray-500">{item.description}</div></div><input type="checkbox" checked={preferences[item.key] === true} onChange={(event) => setPreferences((current) => ({ ...current, [item.key]: event.target.checked }))} className="mt-1 h-4 w-4 rounded border-gray-300" /></label>)}</div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3"><button disabled={busy || !teamId || !channelId} onClick={save} className="rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50">{busy ? 'Saving…' : 'Save Teams settings'}</button><button disabled={busy} onClick={disconnect} className="text-sm font-medium text-red-600 hover:text-red-700">Disconnect Microsoft Teams</button></div>
    </div>
  );
};

export default TeamsIntegration;
